/**
 * Shared Jest sign-in flow.
 *
 * Exchanges the JestSDK signed-player payload for a FantasyPRIME session
 * (accessToken + sessionId) via POST /api/auth/jest, then loads the user via
 * GET /api/auth/me. Used by both the boot auto-login (src/main.tsx) and the
 * manual "Sign in with Jest" button (src/pages/login/index.tsx) so the two
 * paths stay in sync.
 *
 * Guest (unregistered) Jest users receive a full FantasyPRIME account with
 * jestGuest=true. When they later register on Jest, the same stable playerId
 * maps to the same account and jestGuest flips to false (progress transfer).
 */
import { API_BASE } from './api';
import { getDeviceId } from './device-id';
import type { User } from '@/stores/auth.store';

export type JestAuthResult = {
  accessToken: string;
  sessionId: string;
  // null when /api/auth/me failed but the session is otherwise valid; the
  // caller (boot / Navigation) re-fetches the user in that case.
  user: User | null;
};

export async function authenticateWithJest(
  sdk: JestSDKApi,
  opts: { playerSigned?: string } = {},
): Promise<JestAuthResult> {
  const player = await sdk.getPlayer();
  // Callers that just completed registration pass a signed token they already
  // confirmed reports registered=true (see awaitRegisteredSignedPlayer), so the
  // backend reliably clears jestGuest. Boot/sign-in callers omit it and read a
  // fresh token here.
  const playerSigned =
    opts.playerSigned ?? (await sdk.getPlayerSigned()).playerSigned;

  const res = await fetch(`${API_BASE}/api/auth/jest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      playerSigned,
      deviceId: getDeviceId(),
      username: player.username ?? undefined,
    }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || 'Could not sign in with Jest');
  }

  let user: User | null = null;
  try {
    const meRes = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${data.accessToken}` },
    });
    if (meRes.ok) {
      const meData = await meRes.json();
      user = meData.user;
    }
  } catch {
    // session is valid; the user will be hydrated later
  }

  return {
    accessToken: data.accessToken,
    sessionId: data.sessionId,
    user,
  };
}
